/**
 * FocusWorkScreen — session de travail concentrée UNIQUE.
 *
 * Deux phases :
 *   1. "pip"      → fenêtre flottante 320×140 toujours au-dessus, chrono + apps
 *   2. "finalize" → plein écran : saisie OBLIGATOIRE des notes pour terminer
 *
 * Règles strictes :
 *   - Chrono démarré automatiquement, aucune pause possible
 *   - Applications contextuelles uniquement (suggérées par l'IA)
 *   - Notes obligatoires (≥ 20 caractères) pour clore la session
 */
import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { UpgradeGate } from '@components/subscription/UpgradeGate';
import { usePlan } from '@/hooks/usePlan';
import {
  Square,
  Plus,
  Save,
  Mail,
  FileText,
  FileSpreadsheet,
  Presentation,
  Globe,
  Users,
  Calendar,
  Video,
  StickyNote,
  Calculator,
  AppWindow,
  Sparkles,
} from 'lucide-react';
import { UniqueButton } from '@components/UniqueButton';
import { UniqueCard } from '@components/UniqueCard';
import { useTaskStore } from '@store/taskStore';
import { useUserStore } from '@store/userStore';
import { useNavStore } from '@store/navStore';
import { AIService } from '@services/AIService';
import { UniqueBridge } from '@services/uniqueBridge';

/* -------------------------------------------------------------------------- */
/* Mapping app → icône + URI de lancement                                     */
/* -------------------------------------------------------------------------- */

const FALLBACK_APPS = ['Outlook', 'Word', 'Excel', 'Teams', 'Navigateur'];
const MIN_NOTE_LENGTH = 20;

/** Type canonique d'application — utilisé par le pré-remplissage IA. */
export type AppKind =
  | 'email'
  | 'word'
  | 'excel'
  | 'powerpoint'
  | 'teams'
  | 'zoom'
  | 'calendar'
  | 'onenote'
  | 'calculator'
  | 'browser'
  | 'unknown';

interface AppMeta {
  icon: (size: number) => JSX.Element;
  /** Nom canonique passé à launchApp côté Electron. */
  launchTarget: string;
  /** Type d'app pour l'IA de pré-remplissage. */
  kind: AppKind;
}

function metaFor(appName: string): AppMeta {
  const name = appName.toLowerCase();
  if (name.includes('outlook') || name.includes('mail') || name.includes('courriel'))
    return { icon: (s) => <Mail size={s} />, launchTarget: 'outlook', kind: 'email' };
  if (name.includes('word'))
    return { icon: (s) => <FileText size={s} />, launchTarget: 'word', kind: 'word' };
  if (name.includes('excel') || name.includes('tableur'))
    return {
      icon: (s) => <FileSpreadsheet size={s} />,
      launchTarget: 'excel',
      kind: 'excel',
    };
  if (
    name.includes('powerpoint') ||
    name.includes('présentation') ||
    name.includes('presentation')
  )
    return {
      icon: (s) => <Presentation size={s} />,
      launchTarget: 'powerpoint',
      kind: 'powerpoint',
    };
  if (name.includes('teams'))
    return { icon: (s) => <Users size={s} />, launchTarget: 'teams', kind: 'teams' };
  if (name.includes('zoom'))
    return {
      icon: (s) => <Video size={s} />,
      launchTarget: 'zoommtg://',
      kind: 'zoom',
    };
  if (name.includes('calend'))
    return {
      icon: (s) => <Calendar size={s} />,
      launchTarget: 'outlook',
      kind: 'calendar',
    };
  if (name.includes('onenote') || name.includes('note'))
    return {
      icon: (s) => <StickyNote size={s} />,
      launchTarget: 'onenote',
      kind: 'onenote',
    };
  if (name.includes('calc'))
    return {
      icon: (s) => <Calculator size={s} />,
      launchTarget: 'calculator',
      kind: 'calculator',
    };
  if (
    name.includes('navig') ||
    name.includes('browser') ||
    name.includes('edge') ||
    name.includes('chrome')
  )
    return {
      icon: (s) => <Globe size={s} />,
      launchTarget: 'https://www.google.com',
      kind: 'browser',
    };
  return { icon: (s) => <AppWindow size={s} />, launchTarget: appName, kind: 'unknown' };
}

/* -------------------------------------------------------------------------- */
/* Hook partagé : chrono + apps IA + notes + mutations store                  */
/* -------------------------------------------------------------------------- */

interface FocusSessionState {
  totalSeconds: number;
  remaining: number;
  progress: number;
  mm: string;
  ss: string;
  suggestedApps: string[];
  notes: string;
  setNotes: (v: string) => void;
  noteLengthValid: boolean;
  extend5: () => void;
  requestFinish: () => void;
  confirmFinish: () => void;
}

function useFocusSession(): FocusSessionState | null {
  const task = useNavStore((s) => s.focusTask);
  const setFocusPhase = useNavStore((s) => s.setFocusPhase);
  const clearFocus = useNavStore((s) => s.clearFocus);
  const navigate = useNavStore((s) => s.navigate);
  const addFocusMinutes = useTaskStore((s) => s.addFocusMinutes);
  const completeTask = useTaskStore((s) => s.completeTask);
  const defaultMinutes = useUserStore(
    (s) => s.profile?.preferences.defaultFocusMinutes ?? 25,
  );

  const totalSeconds = (task?.estimatedMinutes ?? defaultMinutes) * 60;
  const [remaining, setRemaining] = useState(totalSeconds);
  const [notes, setNotes] = useState('');
  const [suggestedApps, setSuggestedApps] = useState<string[]>(FALLBACK_APPS);
  const tickRef = useRef<number | null>(null);

  useEffect(() => {
    setRemaining(totalSeconds);
  }, [totalSeconds]);

  useEffect(() => {
    if (!task) return;
    tickRef.current = window.setInterval(() => {
      setRemaining((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => {
      if (tickRef.current) window.clearInterval(tickRef.current);
    };
  }, [task]);

  useEffect(() => {
    if (!task) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await AIService.suggestApps({
          task_title: task.title,
          task_description: task.description,
        });
        if (!cancelled && res.apps.length > 0) setSuggestedApps(res.apps);
      } catch {
        /* garde le fallback */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [task]);

  if (!task) return null;

  const progress = 1 - remaining / Math.max(1, totalSeconds);
  const mm = Math.floor(remaining / 60).toString().padStart(2, '0');
  const ss = (remaining % 60).toString().padStart(2, '0');
  const noteLengthValid = notes.trim().length >= MIN_NOTE_LENGTH;

  const extend5 = () => setRemaining((r) => r + 300);

  // Clic Terminer en phase PIP → sortie du PiP + bascule en finalize
  const requestFinish = () => {
    void UniqueBridge.exitFocusPip();
    setFocusPhase('finalize');
  };

  // Clic Terminer en phase Finalize → ne fonctionne que si notes valides
  const confirmFinish = () => {
    const minutesSpent = Math.round((totalSeconds - remaining) / 60);
    if (minutesSpent > 0) addFocusMinutes(task.id, minutesSpent);
    completeTask(task.id);
    clearFocus();
    navigate('dashboard');
  };

  return {
    totalSeconds,
    remaining,
    progress,
    mm,
    ss,
    suggestedApps,
    notes,
    setNotes,
    noteLengthValid,
    extend5,
    requestFinish,
    confirmFinish,
  };
}

/* -------------------------------------------------------------------------- */
/* Composant principal                                                         */
/* -------------------------------------------------------------------------- */

export default function FocusWorkScreen(): JSX.Element {
  // Gate plan — Focus uniquement Essentiel+
  const { limits } = usePlan();
  if (!limits.focusMode) {
    return (
      <UpgradeGate feature="focusMode" requiredPlan="essential"
        message="Le mode Focus Work (chrono PiP + brouillons IA) est disponible à partir du plan Essentiel."
      >
        <></>
      </UpgradeGate>
    );
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const task = useNavStore((s) => s.focusTask);
  const phase = useNavStore((s) => s.focusPhase);
  const navigate = useNavStore((s) => s.navigate);
  const session = useFocusSession();

  // Entrée/sortie du mode PiP Electron
  useEffect(() => {
    if (!task) return;
    if (phase === 'pip') {
      void UniqueBridge.enterFocusPip();
    }
    return () => {
      void UniqueBridge.exitFocusPip();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id, phase]);

  if (!task || !session) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center">
        <UniqueCard padding="lg">
          <h2 className="!text-lg">Aucune tâche active</h2>
          <p className="mt-3 text-sm text-[color:var(--color-text-medium)]">
            Sélectionnez une tâche depuis le tableau de bord pour démarrer une session de focus.
          </p>
          <div className="mt-5">
            <UniqueButton onClick={() => navigate('dashboard')}>
              Aller au tableau de bord
            </UniqueButton>
          </div>
        </UniqueCard>
      </div>
    );
  }

  return phase === 'pip' ? (
    <PipView session={session} task={task} />
  ) : (
    <FinalizeView session={session} taskTitle={task.title} />
  );
}

/* -------------------------------------------------------------------------- */
/* Vue PiP — 320×140                                                           */
/* -------------------------------------------------------------------------- */

function PipView({
  session,
  task,
}: {
  session: FocusSessionState;
  task: { title: string; description?: string };
}): JSX.Element {
  const taskTitle = task.title;
  const [launchFeedback, setLaunchFeedback] = useState<string | null>(null);
  const [loadingApp, setLoadingApp] = useState<string | null>(null);
  const profile = useUserStore((s) => s.profile);

  /**
   * Copie du texte dans le presse-papier, avec fallback via <textarea> si
   * l'API Clipboard n'est pas disponible (certains contextes non sécurisés).
   */
  const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      /* fallback ci-dessous */
    }
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      return true;
    } catch {
      return false;
    }
  };

  /**
   * Encode une URL mailto: avec subject, to, body.
   * Tronque le body si trop long (limite Windows ≈ 2000 caractères pour URL).
   */
  const buildMailto = (to: string | null, subject: string, body: string): string => {
    const safeBody = body.length > 1500 ? body.slice(0, 1500) + '\n\n[...suite dans le presse-papier]' : body;
    const params = new URLSearchParams();
    params.set('subject', subject);
    params.set('body', safeBody);
    const query = params.toString();
    const toPart = to ? encodeURIComponent(to) : '';
    return `mailto:${toPart}?${query}`;
  };

  const handleLaunch = async (appName: string) => {
    const meta = metaFor(appName);
    setLoadingApp(appName);
    setLaunchFeedback(null);

    // 1. Demander un brouillon à l'IA (silencieusement en tâche de fond)
    let draft: Awaited<ReturnType<typeof AIService.draftContent>> | null = null;
    try {
      draft = await AIService.draftContent({
        task_title: task.title,
        task_description: task.description,
        app_kind: meta.kind,
        user_name: profile ? `${profile.firstName} ${profile.lastName}`.trim() : undefined,
      });
    } catch {
      /* si l'IA échoue, on ouvre l'app sans pré-remplissage */
    }

    // 2. Ouvrir l'app selon le type
    let ok = false;
    let feedback = '';

    if (meta.kind === 'email' && draft?.email) {
      // Outlook/mailto : pré-rempli directement via URL scheme
      const mailto = buildMailto(draft.email.to, draft.email.subject, draft.email.body);
      ok = await UniqueBridge.openApp(mailto);
      if (ok && draft.email.body.length > 1500) {
        await copyToClipboard(draft.email.body);
        feedback = 'Email pré-rempli · corps complet copié';
      } else {
        feedback = ok ? 'Email pré-rempli dans Outlook' : 'Échec ouverture Outlook';
      }
    } else if (draft?.clipboard_text) {
      // Excel/Word/OneNote/PowerPoint : copier le contenu puis ouvrir
      const copied = await copyToClipboard(draft.clipboard_text);
      ok = await UniqueBridge.openApp(meta.launchTarget);
      if (copied && ok) {
        feedback =
          meta.kind === 'excel'
            ? 'Tableau copié · Ctrl+V dans Excel'
            : meta.kind === 'word'
              ? 'Texte copié · Ctrl+V dans Word'
              : meta.kind === 'onenote'
                ? 'Note copiée · Ctrl+V dans OneNote'
                : meta.kind === 'powerpoint'
                  ? 'Plan copié · Ctrl+V dans PowerPoint'
                  : `${appName} ouvert · contenu copié`;
      } else {
        feedback = ok ? `${appName} ouvert` : `Échec ${appName}`;
      }
    } else {
      // Pas de draft IA ou app sans pré-remplissage (Teams, Zoom, etc.)
      ok = await UniqueBridge.openApp(meta.launchTarget);
      feedback = ok ? `${appName} ouvert` : `Échec ${appName}`;
    }

    setLoadingApp(null);
    setLaunchFeedback(feedback);
    window.setTimeout(() => setLaunchFeedback(null), 3500);
  };

  const visibleApps = session.suggestedApps.slice(0, 4);
  const isElectron = UniqueBridge.isElectron();

  // En Electron : la fenêtre est déjà redimensionnée à 420×200 par enterFocusPip(),
  // donc on remplit toute la petite fenêtre avec fixed inset-0.
  // En mode web (pas d'Electron) : on simule un PiP flottant en haut-droite
  // avec une taille fixe, pour que le comportement soit cohérent.
  const outerClass = isElectron
    ? 'fixed inset-0 flex items-stretch justify-stretch overflow-hidden'
    : 'fixed top-4 right-4 overflow-hidden z-[9999]';
  const outerStyle: React.CSSProperties = isElectron
    ? { background: 'var(--color-cream)' }
    : {
        width: 420,
        height: 200,
        background: 'var(--color-cream)',
        borderRadius: 12,
        boxShadow:
          '0 20px 50px rgba(0,0,0,0.25), 0 0 0 2px var(--color-primary-dark)',
      };

  return (
    <div className={outerClass} style={outerStyle}>
      <div
        className="flex flex-col w-full h-full"
        style={{
          border: isElectron ? '2px solid var(--color-primary-dark)' : 'none',
          borderRadius: 12,
          background: 'var(--color-white)',
          boxShadow: isElectron ? 'var(--shadow-hover)' : 'none',
        }}
      >
        {/* Bandeau drag — 26px */}
        <div
          className="flex items-center justify-between px-2.5 text-[11px] font-bold uppercase tracking-widest"
          style={{
            height: 26,
            background:
              'linear-gradient(90deg, var(--color-primary), var(--color-primary-dark))',
            color: 'var(--color-text-dark)',
            // @ts-expect-error : CSS vendor property Electron
            WebkitAppRegion: 'drag',
          }}
        >
          <span className="inline-flex items-center gap-1.5">
            <Sparkles size={12} /> UNIQUE · Focus
          </span>
          <button
            type="button"
            onClick={session.requestFinish}
            className="inline-flex items-center justify-center w-5 h-5 rounded-full transition-unique hover:bg-[color:var(--color-urgent)] hover:text-white"
            style={{
              color: 'var(--color-text-dark)',
              // @ts-expect-error Electron property
              WebkitAppRegion: 'no-drag',
            }}
            aria-label="Terminer la session de focus"
            title="Terminer la session"
          >
            <Square size={11} fill="currentColor" />
          </button>
        </div>

        {/* Titre de la tâche — bandeau dédié */}
        <div
          className="px-3 py-1.5 border-b"
          style={{
            background: 'var(--color-primary-glow)',
            borderColor: 'var(--color-border)',
          }}
        >
          <p
            className="text-[12px] font-bold leading-tight line-clamp-2 m-0"
            style={{ color: 'var(--color-text-dark)' }}
            title={taskTitle}
          >
            {taskTitle}
          </p>
        </div>

        {/* Contenu — chrono + apps */}
        <div className="flex flex-1 items-center gap-3 px-3 py-2">
          {/* Chrono compact */}
          <div className="flex-shrink-0">
            <CompactRing
              progress={session.progress}
              mm={session.mm}
              ss={session.ss}
            />
          </div>

          {/* Droite : icônes apps + +5min */}
          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5 flex-wrap">
              {visibleApps.map((app) => {
                const meta = metaFor(app);
                const isLoading = loadingApp === app;
                return (
                  <button
                    key={app}
                    type="button"
                    onClick={() => handleLaunch(app)}
                    disabled={loadingApp !== null}
                    title={isLoading ? `${app} — préparation IA…` : `Ouvrir ${app}`}
                    aria-label={`Ouvrir ${app}`}
                    className="flex items-center justify-center rounded-md transition-unique hover:-translate-y-0.5 disabled:opacity-60"
                    style={{
                      width: 30,
                      height: 30,
                      background: isLoading
                        ? 'var(--color-primary)'
                        : 'var(--color-primary-glow)',
                      color: 'var(--color-primary-dark)',
                      border: '1px solid var(--color-border)',
                      cursor: loadingApp ? 'progress' : 'pointer',
                    }}
                  >
                    {isLoading ? (
                      <span
                        className="inline-block w-3.5 h-3.5 rounded-full border-2 border-current border-t-transparent animate-spin"
                        aria-hidden
                      />
                    ) : (
                      meta.icon(16)
                    )}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={session.extend5}
                className="ml-auto inline-flex items-center gap-0.5 px-2 rounded-md text-[10px] font-bold transition-unique"
                style={{
                  height: 30,
                  background: 'var(--color-primary)',
                  color: 'var(--color-text-dark)',
                  border: '1px solid var(--color-primary-dark)',
                  cursor: 'pointer',
                }}
                aria-label="Ajouter 5 minutes"
              >
                <Plus size={12} /> 5min
              </button>
            </div>

            {launchFeedback && (
              <p
                className="text-[10px] font-semibold m-0 truncate"
                style={{ color: 'var(--color-primary-dark)' }}
              >
                {launchFeedback}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CompactRing({
  progress,
  mm,
  ss,
}: {
  progress: number;
  mm: string;
  ss: string;
}): JSX.Element {
  const size = 82;
  const stroke = 6;
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ * (1 - Math.max(0, Math.min(1, progress)));
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} role="img" aria-label="Chronomètre de focus">
        <defs>
          <linearGradient id="pipRing" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#F5C200" />
            <stop offset="100%" stopColor="#E6A800" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--color-primary-glow)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="url(#pipRing)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 0.8s ease' }}
        />
      </svg>
      <div
        className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
      >
        <span className="text-base font-extrabold tabular-nums leading-none">
          {mm}:{ss}
        </span>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Vue Finalize — plein écran, notes obligatoires                              */
/* -------------------------------------------------------------------------- */

function FinalizeView({
  session,
  taskTitle,
}: {
  session: FocusSessionState;
  taskTitle: string;
}): JSX.Element {
  const [error, setError] = useState<string | null>(null);

  const handleSave = () => {
    setError(null);
    if (!session.noteLengthValid) {
      setError(
        `Les notes rapides sont obligatoires (minimum ${MIN_NOTE_LENGTH} caractères) — laissez une trace de votre session.`,
      );
      return;
    }
    session.confirmFinish();
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <div
        className="rounded-card p-6 mb-6"
        style={{
          background:
            'linear-gradient(135deg, var(--color-primary-glow), var(--color-white))',
          border: '1px solid var(--color-border)',
        }}
      >
        <p className="text-xs uppercase tracking-widest font-bold text-[color:var(--color-primary-dark)] mb-1">
          Session terminée — finalisation
        </p>
        <h1 className="!border-none !pb-0 !text-2xl">{taskTitle}</h1>
        <p className="mt-1 text-[11px] text-[color:var(--color-text-medium)]">
          Temps écoulé : {Math.round((session.totalSeconds - session.remaining) / 60)} min
        </p>
      </div>

      <UniqueCard padding="lg" className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold m-0">
            Notes de la session <span className="text-[color:var(--color-urgent)]">*</span>
          </h3>
          <span
            className="text-xs font-semibold"
            style={{
              color: session.noteLengthValid
                ? 'var(--color-normal)'
                : 'var(--color-text-medium)',
            }}
          >
            {session.notes.trim().length}/{MIN_NOTE_LENGTH}
          </span>
        </div>
        <textarea
          value={session.notes}
          onChange={(e) => {
            session.setNotes(e.target.value);
            if (error) setError(null);
          }}
          rows={8}
          required
          autoFocus
          placeholder="Obligatoire : qu'avez-vous accompli, décidé, ou appris pendant cette session ?"
          className="unique-input resize-none"
          style={{
            borderColor: session.noteLengthValid
              ? 'var(--color-normal)'
              : 'var(--color-border)',
          }}
        />
        <p className="text-xs text-[color:var(--color-text-medium)] mt-2">
          Chaque session doit laisser une trace. Minimum {MIN_NOTE_LENGTH} caractères pour sauvegarder.
        </p>
      </UniqueCard>

      {error && (
        <div
          role="alert"
          className="mb-4 rounded-card px-4 py-3 text-sm font-medium"
          style={{
            background: 'rgba(239,83,80,0.08)',
            border: '1px solid rgba(239,83,80,0.35)',
            color: 'var(--color-urgent)',
          }}
        >
          {error}
        </div>
      )}

      <div className="flex justify-end">
        <UniqueButton
          leftIcon={<Save size={16} />}
          onClick={handleSave}
          size="lg"
          disabled={!session.noteLengthValid}
        >
          Sauvegarder et terminer
        </UniqueButton>
      </div>
    </div>
  );
}
